/**
 * Visible Content Registry
 *
 * Tracks visible article tiles for analytics augmentation.
 * Used to decorate NTP analytics events with visible tile data for CTR measurement.
 */

import debug from '../utils/console'

const logger = debug.withTag('visibleContent')

export interface VisibleContentMetadata {
  headline?: string
  publisher?: string
  url?: string // External article URL
  summary_url?: string // OneNews summary page URL
  category?: string
  type?: 'article' | 'video'
  publishedDate?: string | Date
}

interface VisibleContentEntry {
  id: string
  sortOrder: number
  isVisible: boolean
  metadata?: VisibleContentMetadata
}

const visibleContentRegistry = new Map<string, VisibleContentEntry>()
let sortCounter = 0

/**
 * Register a content item for visibility tracking.
 * Returns the sort order assigned to this item.
 * Note: Re-registering a previously unregistered ID assigns a new sortOrder.
 */
export function registerVisibleContent(
  id: string,
  metadata?: VisibleContentMetadata
): number {
  if (!id) return -1

  // If already registered, update metadata but keep existing sort order
  const existing = visibleContentRegistry.get(id)
  if (existing) {
    if (metadata) {
      existing.metadata = metadata
    }
    return existing.sortOrder
  }

  const sortOrder = sortCounter++
  visibleContentRegistry.set(id, {
    id,
    sortOrder,
    isVisible: false, // Initially not visible until observer confirms
    metadata,
  })

  return sortOrder
}

/**
 * Update the visibility state of a registered content item.
 */
export function setVisibleContent(id: string, isVisible: boolean): void {
  const entry = visibleContentRegistry.get(id)
  if (entry) {
    entry.isVisible = isVisible
  }
}

/**
 * Unregister a content item from visibility tracking.
 */
export function unregisterVisibleContent(id: string): void {
  visibleContentRegistry.delete(id)
}

/**
 * Get all currently visible content items, sorted by their mount order.
 * Returns array with id, sortOrder, and metadata.
 */
export function getVisibleContent(includeOnlyVisible: boolean = true): Array<{
  id: string
  sortOrder: number
  headline?: string
  publisher?: string
  url?: string
  summary_url?: string
  category?: string
  type?: 'article' | 'video'
  story_age?: number
  position_row?: number
  position_col?: number
}> {
  const items = Array.from(visibleContentRegistry.values())
    .filter((item) => !includeOnlyVisible || item.isVisible)
    .sort((a, b) => a.sortOrder - b.sortOrder)

  const cappedResults = items.slice(0, 100)
  if (items.length > 100) {
    logger.warn(`Content exceeds 100 items (${items.length}), capping to 100`)
  }

  return cappedResults.map(({ id, sortOrder, metadata }) => ({
    id,
    sortOrder,
    headline: metadata?.headline,
    publisher: metadata?.publisher,
    url: metadata?.url,
    summary_url: metadata?.summary_url,
    category: metadata?.category,
    type: metadata?.type,
    story_age: metadata?.publishedDate
      ? calculateStoryAge(metadata.publishedDate)
      : undefined,
    // position_row and position_col will be calculated in helper.ts where column_count is available
  }))
}

/**
 * Calculate story age in hours from published date
 */
function calculateStoryAge(publishedDate: string | Date): number {
  const published = new Date(publishedDate)
  const now = new Date()
  const diffMs = now.getTime() - published.getTime()
  return Math.floor(diffMs / (1000 * 60 * 60)) // Convert to hours
}

/**
 * Map visible content items to include position_row and position_col.
 * JIT (Just-In-Time) mapping function that calculates grid positions when column_count is available.
 */
export function mapVisibleContentWithPositions(
  content: Array<{
    id: string
    sortOrder: number
    headline?: string
    publisher?: string
    url?: string
    summary_url?: string
    category?: string
    type?: 'article' | 'video'
    story_age?: number
  }>,
  columnCount: number
): Array<{
  id: string
  sortOrder: number
  headline?: string
  publisher?: string
  url?: string
  summary_url?: string
  category?: string
  type?: 'article' | 'video'
  story_age?: number
  position_row: number
  position_col: number
}> {
  if (!columnCount || columnCount <= 0) {
    // If column_count is invalid, return with 0/0 sentinel values for position fields
    return content.map((item) => ({
      ...item,
      position_row: 0,
      position_col: 0,
    }))
  }

  return content.map((item) => ({
    ...item,
    position_row: Math.floor(item.sortOrder / columnCount),
    position_col: item.sortOrder % columnCount,
  }))
}

/**
 * Check if any content has been registered.
 * Useful for determining if ArticleCards have mounted and registered.
 */
export function hasRegisteredContent(): boolean {
  return visibleContentRegistry.size > 0
}

/**
 * Clear all registered content items.
 * Useful for resetting state after "more news loaded" events.
 */
export function clearVisibleContent(): void {
  visibleContentRegistry.clear()
  sortCounter = 0
}
